import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSaved } from "@/context/SavedContext";
import { Heart, MapPin, Crown, Sparkles, BadgeCheck, Bookmark, BookmarkCheck } from "lucide-react";

export function ProfileCard({ profile, onInterested, showActions = true, index = 0 }) {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const { isSaved, toggle } = useSaved();
  const saved = isSaved ? isSaved(profile.id) : false;

  const chips = [profile.marital_status, profile.profession, profile.sect].filter(Boolean).slice(0, 2);
  const location = [profile.city, profile.country].filter(Boolean).join(", ");
  const open = () => navigate(`/profiles/${profile.id}`);

  const onBookmark = async (e) => {
    e.stopPropagation();
    try {
      const nowSaved = await toggle?.(profile.id);
      toast.success(nowSaved ? "Saved to your list" : "Removed from your saved list");
    } catch { toast.error("Couldn't update your saved list"); }
  };

  return (
    <div
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-emerald-900/10 bg-white shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`profile-card-${profile.id}`}
    >
      <div
        className="relative aspect-[4/5] cursor-pointer overflow-hidden bg-emerald-900"
        onClick={open}
        data-testid={`profile-card-photo-${profile.id}`}
      >
        {profile.photo && !imgError ? (
          <img src={profile.photo} alt={profile.name} onError={() => setImgError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-emerald-800 to-emerald-950 text-6xl font-serif text-amber-300/60">
            {profile.name?.[0] || "?"}
          </div>
        )}

        {typeof profile.match_percent === "number" && (
          <div className="glass absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-emerald-950/55 px-3 py-1.5 text-xs font-bold text-white shadow-sm" data-testid={`match-percent-${profile.id}`}>
            <Sparkles className="h-3.5 w-3.5 text-amber-300" /> {profile.match_percent}% Match
          </div>
        )}

        <button
          onClick={onBookmark}
          data-testid={`bookmark-btn-${profile.id}`}
          aria-label={saved ? "Remove from saved" : "Save profile"}
          className={`glass absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full shadow-sm transition-colors ${saved ? "bg-amber-500 text-white" : "bg-white/85 text-emerald-800 hover:bg-white"}`}
        >
          {saved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
        </button>

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/40 to-transparent p-4 pt-12">
          <h3 className="flex items-center gap-1.5 font-serif text-2xl font-bold leading-tight text-white">
            {profile.name}{profile.age ? `, ${profile.age}` : ""}
            {profile.verified && <BadgeCheck className="h-5 w-5 text-amber-300" />}
            {profile.is_premium && <Crown className="h-4 w-4 text-amber-300" />}
          </h3>
          {location && (
            <p className="mt-0.5 flex items-center gap-1 text-sm text-emerald-50/90">
              <MapPin className="h-3.5 w-3.5" /> {location}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {chips.map((c) => (
              <span key={c} className="rounded-full bg-mint px-3 py-1 text-xs font-medium text-emerald-800">{c}</span>
            ))}
          </div>
        )}
        {profile.bio && <p className="line-clamp-3 text-sm leading-relaxed text-slate-600">{profile.bio}</p>}

        <div className="mt-auto flex gap-2 pt-1">
          <button
            onClick={open}
            data-testid={`view-profile-btn-${profile.id}`}
            className="flex-1 rounded-xl bg-mint px-4 py-2.5 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-100"
          >
            View Profile
          </button>
          {showActions && (
            <button
              onClick={() => onInterested?.(profile.id)}
              data-testid={`interested-btn-${profile.id}`}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-800 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-900"
            >
              <Heart className="h-4 w-4 fill-current" /> Interested
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
